import { cn } from './cn';

/**
 * The placeholder every route renders before its data arrives (Phase 7 DoD: "every route
 * renders a skeleton, never a blank screen").
 *
 * `aria-hidden` throughout, with one live region per page announcing the load instead: a
 * screen reader reading out twelve empty boxes is worse than silence.
 */
export function Skeleton({
  className,
  rounded = 'md',
}: {
  className?: string;
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'full';
}) {
  const radius = {
    none: '',
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    full: 'rounded-full',
  }[rounded];
  return <div aria-hidden="true" className={cn('nk-skeleton', radius, className)} />;
}

/** A paragraph of skeleton lines, the last one short so it reads as text rather than a box. */
export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div className={cn('flex flex-col gap-2', className)} aria-hidden="true">
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton
          key={index}
          rounded="sm"
          className={cn('h-3', index === lines - 1 ? 'w-2/3' : 'w-full')}
        />
      ))}
    </div>
  );
}

/**
 * Wraps a loading region and announces it once. `aria-busy` moves to `false` when the
 * children are real content, so assistive technology hears "loading" exactly once per
 * route rather than per card.
 */
export function LoadingRegion({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className} role="status" aria-busy="true" aria-live="polite" aria-label={label}>
      {children}
    </div>
  );
}
